import { useState, useEffect } from "react";
import "./FaqCard.css";
import {
  fetchContent,
  getUserDetails,
  updateContent,
  createContent,
  deleteContent,
} from "../../Utils/functions";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";

export const FaqCard = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);
  const [isMedRPTAdmin, setIsMedRPTAdmin] = useState(false);
  const [editingFaqId, setEditingFaqId] = useState(null);
  const [newFaq, setNewFaq] = useState({
    question: "",
    answer: "",
    isActive: true,
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user && user.userId) {
        try {
          const userDetails = await getUserDetails();
          setIsMedRPTAdmin(userDetails?.user?.isMedRPTAdmin || false);
        } catch (error) {
          console.error("Error checking admin status:", error);
          setIsMedRPTAdmin(false);
        }
      }
    };
    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    const fetchFaqData = async () => {
      try {
        setLoading(true);
        const data = await fetchContent("FAQ");
        if (data && Array.isArray(data)) {
          setFaqs(data);
        } else {
          setFaqs([]);
        }
        setError(null);
      } catch (error) {
        console.error("Failed to fetch FAQs", error);
        setError(null);
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqData();
  }, []);

  const toggleFaq = (index) => {
    setExpandedQuestionIndex(expandedQuestionIndex === index ? null : index);
  };

  const handleToggleActive = async (index, e) => {
    e.stopPropagation();
    const faq = faqs[index];
    const currentActiveStatus = faq.isActive !== false;
    const newActiveStatus = !currentActiveStatus;

    const updatedFaqs = [...faqs];
    updatedFaqs[index] = { ...updatedFaqs[index], isActive: newActiveStatus };
    setFaqs(updatedFaqs);

    try {
      setSaving(true);
      await updateContent(faq.question, faq.answer, newActiveStatus, faq.id, "FAQ");
      setSnackbar({
        open: true,
        message: `FAQ ${newActiveStatus ? "activated" : "deactivated"
          } successfully`,
        severity: "success",
      });
    } catch (error) {
      const originalFaqs = [...faqs];
      originalFaqs[index] = {
        ...originalFaqs[index],
        isActive: currentActiveStatus,
      };
      setFaqs(originalFaqs);
      setSnackbar({
        open: true,
        message: "Failed to update FAQ status: " + error.message,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (index) => {
    const faq = faqs[index];
    setNewFaq({
      question: faq.question,
      answer: faq.answer,
      isActive: faq.isActive !== false,
    });
    setEditingFaqId(faq.id);
    setShowAddForm(true);
  };

  const handleDeleteClick = (index) => {
    setFaqToDelete(index);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (faqToDelete === null) return;
    const faq = faqs[faqToDelete];
    try {
      setSaving(true);
      await deleteContent(faq.id);
      setFaqs(faqs.filter((_, i) => i !== faqToDelete));
      setDeleteDialogOpen(false);
      setFaqToDelete(null);
      setSnackbar({
        open: true,
        message: "FAQ deleted successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to delete FAQ: " + error.message,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
  };

  const handleAdd = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      setSnackbar({
        open: true,
        message: "Please fill in both question and answer",
        severity: "warning",
      });
      return;
    }

    const isEditing = editingFaqId !== null;

    try {
      setSaving(true);
      setSnackbar({
        open: true,
        message: isEditing ? "Updating FAQ..." : "Adding new FAQ...",
        severity: "info",
      });

      if (isEditing) {
        await updateContent(
          newFaq.question,
          newFaq.answer,
          newFaq.isActive,
          editingFaqId,
          "FAQ"
        );
        const updatedFaqs = faqs.map((faq) =>
          faq.id === editingFaqId
            ? {
              ...faq,
              question: newFaq.question,
              answer: newFaq.answer,
            }
            : faq
        );
        setFaqs(updatedFaqs);
      } else {
        await createContent(newFaq.question, newFaq.answer, newFaq.isActive, "FAQ");
        const data = await fetchContent("FAQ");
        if (data && Array.isArray(data)) {
          setFaqs(data);
        }
      }

      setNewFaq({ question: "", answer: "", isActive: true });
      setEditingFaqId(null);
      setShowAddForm(false);
      setSnackbar({
        open: true,
        message: isEditing
          ? "FAQ updated successfully!"
          : "FAQ added successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: isEditing
          ? "Failed to update FAQ: " + error.message
          : "Failed to create FAQ: " + error.message,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    < >
      <div className="faq-content-wrapper">
        <div className="faq-content">
          {loading ? (
            <div className="faq-loading">Loading...</div>
          ) : error ? (
            <div className="faq-error">{error}</div>
          ) : (
            faqs.map((faq, index) => {
              const isExpanded = expandedQuestionIndex === index;

              return (
                <div key={faq.id || index} className="faq-item">
                  <>
                    <div
                      className="faq-question"
                      onClick={() => toggleFaq(index)}
                    >
                      <span
                        className={`faq-toggle-icon ${isExpanded ? "expanded" : ""
                          }`}
                      >
                        {isExpanded ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 10H16"
                              stroke="#49994C"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 4V16M4 10H16"
                              stroke="#49994C"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </span>
                      <span
                        className="faq-question-text"
                        dangerouslySetInnerHTML={{ __html: faq.question }}
                      />
                      {isMedRPTAdmin && (
                        <div className="faq-admin-actions">
                          <button
                            className="faq-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(index);
                            }}
                          >
                            Edit
                          </button>
                          <div
                            className="faq-active-toggle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <label className="faq-toggle-switch">
                              <input
                                type="checkbox"
                                checked={Boolean(faq.isActive !== false)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleActive(index, e);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                disabled={saving}
                                key={`toggle-${faq.id}-${faq.isActive}`}
                              />
                              <span className="faq-toggle-slider"></span>
                            </label>
                            <span className="faq-toggle-label">
                              {faq.isActive !== false
                                ? "Active"
                                : "Inactive"}
                            </span>
                          </div>

                          <button
                            className="faq-delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(index);
                            }}
                            disabled={saving}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    {isExpanded && (
                      <div
                        className="faq-answer"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    )}
                  </>
                </div>
              );
            })
          )}
          {isMedRPTAdmin && (
            <button
              className="faq-add-button"
              onClick={() => {
                setNewFaq({ question: "", answer: "", isActive: true });
                setEditingFaqId(null);
                setShowAddForm(true);
              }}
            >
              + Add FAQ
            </button>
          )}
        </div>
      </div>
      {showAddForm && (
        <Dialog
          open={showAddForm}
          onClose={() => {
            setShowAddForm(false);
            setNewFaq({ question: "", answer: "", isActive: true });
            setEditingFaqId(null);
          }}
          aria-labelledby="add-faq-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="add-faq-dialog-title">
            {editingFaqId ? "Edit FAQ" : "Add New FAQ"}
          </DialogTitle>
          <DialogContent
            sx={{
              minWidth: "500px",
              padding: "24px",
              "@media (max-width: 768px)": {
                minWidth: "auto",
                padding: "16px",
              },
            }}
          >
            <TextField
              value={newFaq.question}
              onChange={(e) =>
                setNewFaq({ ...newFaq, question: e.target.value })
              }
              placeholder="Enter question..."
              fullWidth
              sx={{ marginBottom: "20px" }}
            />
            <TextField
              value={newFaq.answer}
              onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
              placeholder="Enter answer..."
              multiline
              rows={8}
              fullWidth
              sx={{ marginBottom: "20px" }}
            />
            {isMedRPTAdmin && (
              <div className="faq-active-toggle" style={{ marginTop: "10px" }}>
                <label className="faq-toggle-switch">
                  <input
                    type="checkbox"
                    checked={newFaq.isActive !== false}
                    onChange={(e) =>
                      setNewFaq({ ...newFaq, isActive: e.target.checked })
                    }
                    disabled={saving}
                  />
                  <span className="faq-toggle-slider"></span>
                </label>
                <span className="faq-toggle-label">
                  {newFaq.isActive !== false ? "Active" : "Inactive"}
                </span>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowAddForm(false);
                setNewFaq({ question: "", answer: "", isActive: true });
                setEditingFaqId(null);
              }}
              sx={{
                color: "#49994C",
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              variant="contained"
              sx={{
                backgroundColor: "#49994C",
                "&:hover": {
                  backgroundColor: "#3d7d40",
                },
              }}
              disabled={saving}
            >
              {saving
                ? editingFaqId
                  ? "Updating..."
                  : "Adding..."
                : editingFaqId
                  ? "Update FAQ"
                  : "Add FAQ"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-faq-dialog-title"
      >
        <DialogTitle id="delete-faq-dialog-title">Delete FAQ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this FAQ? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              color: "#49994C",
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
